import React, { useMemo } from 'react'
import type { CSSProperties } from 'react'
import type { DonutChartProps } from './DonutChart.types'
import {
  DONUT_CHART_DEFAULT_SIZE,
  DONUT_CHART_DEFAULT_TITLE,
  DONUT_CHART_DEFAULT_VALUE,
  DONUT_CHART_INNER_RADIUS_RATIO,
  DONUT_CHART_PROGRESS_COLOR,
  DONUT_CHART_TRACK_COLOR,
  DONUT_CHART_VALUE_COLOR,
} from './DonutChart.types'
import './DonutChart.css'

const clampPercent = (value: number): number =>
  Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0))

const DonutChart: React.FC<DonutChartProps> = ({
  value = DONUT_CHART_DEFAULT_VALUE,
  title = DONUT_CHART_DEFAULT_TITLE,
  hideTitle = false,
  valueLabel,
  hideValue = false,
  size = DONUT_CHART_DEFAULT_SIZE,
  trackColor = DONUT_CHART_TRACK_COLOR,
  progressColor = DONUT_CHART_PROGRESS_COLOR,
  darkMode = false,
  className = '',
  style,
  'aria-label': ariaLabel,
}) => {
  const resolvedValue = clampPercent(value)
  const displayValue = valueLabel ?? `${Math.round(resolvedValue)}%`

  const geometry = useMemo(() => {
    const cx = size / 2
    const cy = size / 2
    const outerRadius = size / 2
    const strokeWidth = outerRadius * (1 - DONUT_CHART_INNER_RADIUS_RATIO)
    const radius = outerRadius - strokeWidth / 2
    const circumference = 2 * Math.PI * radius
    const progressLength = (resolvedValue / 100) * circumference

    return {
      cx,
      cy,
      radius,
      strokeWidth,
      circumference,
      progressLength,
    }
  }, [resolvedValue, size])

  const classNames = [
    'ds-donut-chart',
    darkMode ? 'ds-donut-chart--dark' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const rootStyle = {
    ...style,
    '--ds-donut-chart-size': `${size}px`,
    '--ds-donut-chart-value-color': DONUT_CHART_VALUE_COLOR,
  } as CSSProperties

  const accessibleLabel =
    ariaLabel ?? [title, displayValue].filter(Boolean).join(', ')

  return (
    <div
      className={classNames}
      style={rootStyle}
      role="img"
      aria-label={accessibleLabel}
    >
      <svg
        className="ds-donut-chart__svg"
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        aria-hidden="true"
      >
        <circle
          cx={geometry.cx}
          cy={geometry.cy}
          r={geometry.radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={geometry.strokeWidth}
        />
        {resolvedValue > 0 ? (
          <circle
            cx={geometry.cx}
            cy={geometry.cy}
            r={geometry.radius}
            fill="none"
            stroke={progressColor}
            strokeWidth={geometry.strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${geometry.progressLength} ${geometry.circumference}`}
            transform={`rotate(-90 ${geometry.cx} ${geometry.cy})`}
          />
        ) : null}
      </svg>

      {!hideTitle || !hideValue ? (
        <div className="ds-donut-chart__center">
          {!hideTitle ? <p className="ds-donut-chart__title">{title}</p> : null}
          {!hideValue ? (
            <p className="ds-donut-chart__value">{displayValue}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default DonutChart
